import { pickT } from "@repo/shared";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { DefaultPortfolioTypeSelect } from "./default-portfolio-type-select";
import { T } from "./portfolio-input-config-form.i18n";

type PortfolioTypeOption = "model" | "custom" | "dynamic";

type PortfolioInputConfigFormProps = {
  flowLanguage: string;
  allowedPortfolioTypes: PortfolioTypeOption[];
  onAllowedPortfolioTypesChange: (types: PortfolioTypeOption[]) => void;
  defaultPortfolioType: PortfolioTypeOption | undefined;
  onDefaultPortfolioTypeChange: (type: PortfolioTypeOption | undefined) => void;
  showPortfolioName: boolean;
  onShowPortfolioNameChange: (show: boolean) => void;
  showDateInputs: boolean;
  onShowDateInputsChange: (show: boolean) => void;
};

export function PortfolioInputConfigForm({
  flowLanguage,
  allowedPortfolioTypes,
  onAllowedPortfolioTypesChange: setAllowedPortfolioTypes,
  defaultPortfolioType,
  onDefaultPortfolioTypeChange,
  showPortfolioName,
  onShowPortfolioNameChange,
  showDateInputs,
  onShowDateInputsChange,
}: PortfolioInputConfigFormProps) {
  const t = pickT(T, flowLanguage);
  // Unticking the default type sends it back to automatic, never to another type.
  const onAllowedPortfolioTypesChange = (types: PortfolioTypeOption[]) => {
    setAllowedPortfolioTypes(types);
    if (defaultPortfolioType && !types.includes(defaultPortfolioType)) {
      onDefaultPortfolioTypeChange(undefined);
    }
  };

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
      <div className="space-y-2">
        <Label className="text-base">{t.allowedPortfolioTypes}</Label>
        <p className="text-sm text-muted-foreground">{t.portfolioTypesDesc}</p>
      </div>

      <div className="space-y-2">
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="allow-model"
            checked={allowedPortfolioTypes.includes("model")}
            onChange={(e) => {
              if (e.target.checked) {
                if (!allowedPortfolioTypes.includes("model")) {
                  onAllowedPortfolioTypesChange([...allowedPortfolioTypes, "model"]);
                }
              } else {
                onAllowedPortfolioTypesChange(allowedPortfolioTypes.filter((t) => t !== "model"));
              }
            }}
            className="h-4 w-4"
          />
          <Label htmlFor="allow-model" className="text-sm font-normal cursor-pointer">
            {t.modelPortfolio}
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="allow-custom"
            checked={allowedPortfolioTypes.includes("custom")}
            onChange={(e) => {
              if (e.target.checked) {
                if (!allowedPortfolioTypes.includes("custom")) {
                  onAllowedPortfolioTypesChange([...allowedPortfolioTypes, "custom"]);
                }
              } else {
                onAllowedPortfolioTypesChange(allowedPortfolioTypes.filter((t) => t !== "custom"));
              }
            }}
            className="h-4 w-4"
          />
          <Label htmlFor="allow-custom" className="text-sm font-normal cursor-pointer">
            {t.customPortfolio}
          </Label>
        </div>
        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            id="allow-dynamic"
            checked={allowedPortfolioTypes.includes("dynamic")}
            onChange={(e) => {
              if (e.target.checked) {
                if (!allowedPortfolioTypes.includes("dynamic")) {
                  onAllowedPortfolioTypesChange([...allowedPortfolioTypes, "dynamic"]);
                }
              } else {
                onAllowedPortfolioTypesChange(allowedPortfolioTypes.filter((t) => t !== "dynamic"));
              }
            }}
            className="h-4 w-4"
          />
          <Label htmlFor="allow-dynamic" className="text-sm font-normal cursor-pointer">
            {t.dynamicPortfolio}
          </Label>
        </div>
      </div>

      {allowedPortfolioTypes.length === 0 && (
        <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
          {t.atLeastOnePortfolioType}
        </div>
      )}

      <DefaultPortfolioTypeSelect
        flowLanguage={flowLanguage}
        allowedPortfolioTypes={allowedPortfolioTypes}
        defaultPortfolioType={defaultPortfolioType}
        onDefaultPortfolioTypeChange={onDefaultPortfolioTypeChange}
      />

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="portfolio-input-show-name" className="text-base">
            {t.showPortfolioName}
          </Label>
          <p className="text-sm text-muted-foreground">{t.portfolioNameDesc}</p>
        </div>
        <Switch
          id="portfolio-input-show-name"
          checked={showPortfolioName}
          onCheckedChange={onShowPortfolioNameChange}
        />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <Label htmlFor="portfolio-input-show-dates" className="text-base">
            {t.showDateInputs}
          </Label>
          <p className="text-sm text-muted-foreground">{t.dateInputsDesc}</p>
        </div>
        <Switch
          id="portfolio-input-show-dates"
          checked={showDateInputs}
          onCheckedChange={onShowDateInputsChange}
        />
      </div>
    </div>
  );
}
